import { DateRangeSchema } from 'common/hooks/customSearchParams';
import { useFilterSearchParams } from 'pages/Shipments/Containers/utilities';
import { DEFAULT_DATE_RANGE_PARAM } from 'pages/Shipments/params';
import {
  GatedOutDateRange,
  GatedOutFilterKind,
  getGatedOutFilters,
} from 'pages/TrackAndTrace/components/controls/filters/parsers/gated-out';
import * as React from 'react';
import { Checkbox } from 'components/tailwind-ui/Checkbox';
import { DateInput } from 'components/tailwind-ui/DateInput';
import { Section } from './Section';

type Option = 'today' | 'yesterday' | 'moreThanFive' | 'lessThanFive';

const GATED_OUT_OPTIONS: { label: string; value: Option }[] = [
  { label: 'Gated out today', value: 'today' },
  { label: 'Gated out yesterday', value: 'yesterday' },
  { label: 'Gated out for more than 5 days', value: 'moreThanFive' },
  { label: 'Not gated out for more than 5 days', value: 'lessThanFive' },
];

interface State {
  today: boolean;
  yesterday: boolean;
  moreThanFive: boolean;
  lessThanFive: boolean;
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}

export const GatedOut: React.FC = () => {
  const { searchParams, setSearchParams } = useFilterSearchParams(true);

  const filterStates: State = React.useMemo(() => {
    const filters = getGatedOutFilters(searchParams);

    const rangeFilter = filters.find((f) => f.kind === GatedOutFilterKind.DateRange) as
      | GatedOutDateRange
      | undefined;

    return {
      moreThanFive: !!filters.find((f) => f.kind === GatedOutFilterKind.MoreThanFive),
      lessThanFive: !!filters.find((f) => f.kind === GatedOutFilterKind.LessThanFive),
      today: !!filters.find((f) => f.kind === GatedOutFilterKind.Today),
      yesterday: !!filters.find((f) => f.kind === GatedOutFilterKind.Yesterday),
      startDate: rangeFilter ? rangeFilter.startDate : null,
      endDate: rangeFilter ? rangeFilter.endDate : null,
    };
  }, [searchParams]);

  const handleChange = (value: Option, checked: boolean) => {
    const newFilters = {
      moreThanFive: false,
      lessThanFive: false,
      today: false,
      yesterday: false,
      [value]: checked,
    };

    if (checked) {
      if (newFilters.moreThanFive) {
        setSearchParams({
          gatedOutAt: {
            ...DEFAULT_DATE_RANGE_PARAM,
            endDate: '-5:days',
          },
        });
      } else if (newFilters.lessThanFive) {
        setSearchParams({
          gatedOutAt: {
            ...DEFAULT_DATE_RANGE_PARAM,
            startDate: '-5:days',
          },
        });
      } else if (newFilters.today) {
        setSearchParams({
          gatedOutAt: {
            ...DEFAULT_DATE_RANGE_PARAM,
            exactDate: 'today',
          },
        });
      } else if (newFilters.yesterday) {
        setSearchParams({
          gatedOutAt: {
            ...DEFAULT_DATE_RANGE_PARAM,
            exactDate: '-1:days',
          },
        });
      }
    } else {
      setSearchParams({ gatedOutAt: null });
    }
  };

  const handleDateChange = (date: moment.Moment | null, kind: 'start' | 'end'): boolean => {
    let { startDate, endDate } = filterStates;

    switch (kind) {
      case 'start':
        if (date && endDate && date.isAfter(endDate)) {
          endDate = null;
        }
        startDate = date;
        break;
      case 'end':
        if (date && startDate && date.isBefore(startDate)) {
          startDate = null;
        }
        endDate = date;
        break;
      default:
        break;
    }
    const gatedOutAt: DateRangeSchema = { startDate, endDate };

    setSearchParams({ gatedOutAt });

    return true;
  };

  return (
    <Section title="Gated out date filters">
      <div className="flex flex-wrap items-center">
        {GATED_OUT_OPTIONS.map(({ label, value }) => (
          <Checkbox
            id={`gated-out-${value}`}
            key={value}
            label={label}
            checked={filterStates[value]}
            onCheck={(checked) => handleChange(value, checked)}
            className="mr-6"
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center mt-3">
        <DateInput
          label="On / after"
          date={filterStates.startDate}
          onChange={(date) => handleDateChange(date, 'start')}
          checked={!!filterStates.startDate}
          style={{ marginLeft: 0, marginRight: 32 }}
        />
        <DateInput
          label="On / before"
          date={filterStates.endDate}
          onChange={(date) => handleDateChange(date, 'end')}
          checked={!!filterStates.endDate}
          style={{ marginLeft: 0, marginRight: 32 }}
        />
      </div>
    </Section>
  );
};
GatedOut.displayName = 'Filters.More.GatedOut';
